<template>
  <div class="article-header">
    <div>
      <span
        v-if="showContentTag"
        :class="!newArticleDetailsDisplay && 'article-header__font-smaller'"
      >
        <ContentTag
          :class="
            newArticleDetailsDisplay && 'article-header__content-tag-font'
          "
          :article="article"
          :use-gray-background-color="newArticleDetailsDisplay"
          :font-smaller="!newArticleDetailsDisplay"
          :font-weight-bold="!newArticleDetailsDisplay"
          :rounded-pill="newArticleDetailsDisplay"
        />
      </span>

      <span
        v-if="article.community"
        :class="!newArticleDetailsDisplay && 'article-header__font-smaller'"
      >
        <ContentTagLink
          :class="
            newArticleDetailsDisplay && 'article-header__content-tag-font'
          "
          :tag-text="article.community.name"
          :link="`/engage/coe/community-details/${article.community.id}`"
          :font-smaller="!newArticleDetailsDisplay"
          :font-weight-bold="!newArticleDetailsDisplay"
          :rounded-pill="newArticleDetailsDisplay"
        />
      </span>

      <Authors :list-source="listSource" />

      <div
        v-if="
          (showNonPeerReviewedStatus || newArticleDetailsDisplay) &&
          partner !== 'miir'
        "
        class="my-2"
      >
        <div class="d-flex justify-start align-center">
          <img alt="" src="~/static/images/NonPeerReviewed.svg" class="mr-2" />
          <span
            v-if="newArticleDetailsDisplay"
            class="article-header__peer-review-notice my-3"
          >
            {{ nonPeerReviewedMessage }}
          </span>
          <span v-else class="article-header__font-smaller"
            >Non Peer Reviewed</span
          >
        </div>
      </div>
    </div>

    <div class="d-flex flex-column flex-wrap flex-sm-row mb-2 mb-sm-0">
      <ArticleHeaderButton
        v-if="videoMimeType && newArticleDetailsDisplay"
        aria-label="Play video, go to video section"
        button-text="Play"
        emit-on-click="start-video"
        :outlined="false"
        data-test-id="PlayButton"
        v-on="$listeners"
      >
        <PlayVideoIcon />
      </ArticleHeaderButton>

      <ArticleHeaderDownloadButton
        v-if="newArticleDetailsDisplay"
        :article="article"
        :video-mime-type="videoMimeType"
      />

      <CiteButton
        v-if="newArticleDetailsDisplay"
        :article="article"
        :citation-style="citationStyle"
      />

      <ArticleHeaderButton
        v-if="articleDetailsShowComments"
        :button-text="commentsButtonText"
        :aria-label="`${commentsButtonText}, go to comments section`"
        emit-on-click="scroll-to-comment-section"
        data-test-id="JumptoCommentButton"
        v-on="$listeners"
      >
        <v-icon :class="newArticleDetailsDisplay ? 'mr-1' : 'mr-2'">
          {{ mdiChatOutline }}
        </v-icon>
      </ArticleHeaderButton>
    </div>

    <QuestionDetails v-if="article.question" :article="article" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { mdiChatOutline } from "@mdi/js";
import mapFlags from "~/plugins/utilities/mapFlags";
import sorted from "~/plugins/utilities/sorted";

import ContentTag from "~/components/articles/components/ContentTag.vue";
import ContentTagLink from "~/components/articles/components/ContentTagLink.vue";
import Authors from "~/components/articles/components/Authors.vue";
import QuestionDetails from "~/components/articles/components/QuestionDetails.vue";
import ArticleHeaderButton from "~/components/articles/ArticleHeaderButton.vue";
import ArticleHeaderDownloadButton from "~/components/articles/ArticleHeaderDownloadButton.vue";
import CiteButton from "~/components/articles/components/CiteButton";
import PlayVideoIcon from "~/components/articles/components/icons/PlayVideoIcon";

export default {
  name: "ArticleHeader",
  components: {
    ContentTag,
    ContentTagLink,
    Authors,
    QuestionDetails,
    ArticleHeaderButton,
    ArticleHeaderDownloadButton,
    CiteButton,
    PlayVideoIcon,
  },
  props: {
    article: { type: Object, required: true },
    citationStyle: { type: Object, default: undefined },
  },
  data: () => {
    return {
      mdiChatOutline,
    };
  },
  computed: {
    ...mapState({
      commentChipColor: (state) =>
        state.theme.layout.articleDetailsDownloadButtonColor,
    }),
    // TODO: Once newArticleDetailsDisplay has been rolled out to all partners,
    // use showNonPeerReviewedStatus to hide non peer reviewed status section for MIIR instead of (partner !== 'miir')
    ...mapFlags([
      "articleDetailsShowComments",
      "showContentTypeArticle",
      "showNonPeerReviewedStatus",
      "newArticleDetailsDisplay",
      "showPartnerNonPeerReviewedMessage",
    ]),

    listSource() {
      const authors = this.article.authors || [];

      return sorted(authors, "displayOrder");
    },
    showContentTag() {
      return this.showContentTypeArticle;
    },
    commentsButtonText() {
      return `Comment ${
        this.article.commentsCount > 0 ? `(${this.article.commentsCount})` : ""
      }`;
    },
    videoMimeType() {
      return (
        this.article.asset && this.article?.asset.mimeType.includes("video")
      );
    },
    partner() {
      return this.$config.partner;
    },
    nonPeerReviewedMessage() {
      if (this.showPartnerNonPeerReviewedMessage) {
        if (this.partner === "apsa") {
          return "This content is an early or alternative research output and has not been peer-reviewed at the time of posting.";
        }

        return "This content is a preprint and has not undergone peer review at the time of posting.";
      }

      return "This content is an early or alternative research output and has not been peer-reviewed by Cambridge University Press at the time of posting.";
    },
  },
  methods: {
    onClick() {
      this.$emit("on-click");
    },
  },
};
</script>

<style lang="scss" scoped>
.article-header {
  a span {
    cursor: pointer;
  }
  &__comments-count {
    text-decoration: underline !important;
  }
  &__font-smaller {
    font-size: smaller;
  }
  &__content-tag-font {
    font-size: 14px;
  }
  &__peer-review-notice {
    line-height: 20px;
    color: #272727;
  }
}
</style>
